import React, { useEffect, useRef, useState } from 'react'
import { 
  FiMoreVertical, 
  FiSearch, 
  FiFilter, 
  FiDownload, 
  FiEye, 
  FiRefreshCw,
  FiCalendar,
  FiClock,
  FiTag,
  FiUser,
  FiDollarSign,
  FiCheckCircle,
  FiXCircle,
  FiClock as FiClockIcon,
  FiAlertCircle,
  FiArrowUp,
  FiArrowDown,
  FiTrendingUp,
  FiTrendingDown,
  FiCopy,
  FiFileText,
  FiGrid,
  FiList
} from "react-icons/fi";
import { 
  MdOutlineFileDownload, 
  MdOutlineVisibility,
  MdOutlineRefresh,
  MdOutlineFilter,
  MdOutlineSearch,
  MdOutlineCalendarToday,
  MdOutlineAccessTime,
  MdOutlinePerson,
  MdOutlineAttachMoney,
  MdOutlineCheckCircle,
  MdOutlineCancel,
  MdOutlinePending
} from "react-icons/md";
import { HiOutlineDotsVertical } from "react-icons/hi";
import moment from 'moment';
import { CiFilter, CiSearch } from 'react-icons/ci';
import { PiFileCsvDuotone } from 'react-icons/pi';
import DatePicker from '../../../components/ui/FromDateToDate';
import { CSVLink } from 'react-csv';
import ReactPaginate from 'react-paginate';
import SortableHeader from '../../../components/SortableHeader';

const PaymentTransTable = ({ 
  filter, 
  setFilter, 
  TbaleName, 
  TbaleHeader, 
  TbaleData, 
  showMenu, 
  viewAction, 
  copyId, 
  handleRefundPayment, 
  isMenu, 
  clickedUserId, 
  handlePageClick, 
  total,
  currentSortKey,
  currentSortOrder,
  onSort
}) => {
  const ContentActionRef = useRef(null)
  const [searchTerm, setSearchTerm] = useState(filter?.keyWord || '');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ContentActionRef.current && !ContentActionRef.current.contains(event.target)) {
        showMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Prepare CSV data
  const csvData = [
    TbaleHeader.map(h => h.label),
    ...TbaleData.map((ele, index) => [
      index + 1,
      ele._id?.slice(0, 7) || '',
      ele.type || '',
      ele?.user?.fullName || '',
      moment(ele.createdAt).format('MMMM D, YYYY'),
      ele.status || '',
      ele.amount?.toFixed(2) || '',
    ])
  ];

  const handleSetFilter = (name, value) => {
    setFilter((p) => ({
      ...p,
      [name]: value
    }));
  }

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'SUCCESS': { 
        icon: FiCheckCircle, 
        color: 'text-emerald-700 bg-emerald-50 border-emerald-200', 
        label: 'Success' 
      },
      'COMPLETED': { 
        icon: FiCheckCircle, 
        color: 'text-emerald-700 bg-emerald-50 border-emerald-200', 
        label: 'Completed' 
      },
      'PENDING': { 
        icon: FiClockIcon, 
        color: 'text-amber-700 bg-amber-50 border-amber-200', 
        label: 'Pending' 
      },
      'FAILED': { 
        icon: FiXCircle, 
        color: 'text-rose-700 bg-rose-50 border-rose-200', 
        label: 'Failed' 
      },
      'REFUND': { 
        icon: FiArrowUp, 
        color: 'text-blue-700 bg-blue-50 border-blue-200', 
        label: 'Refunded' 
      },
      'REFUNDED': { 
        icon: FiArrowUp, 
        color: 'text-blue-700 bg-blue-50 border-blue-200', 
        label: 'Refunded' 
      }
    };
    const config = statusConfig[status?.toUpperCase()] || statusConfig['PENDING'];
    const Icon = config.icon;
    
    return (
      <span className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  // Transaction type badge
  const TypeBadge = ({ type }) => {
    const typeConfig = {
      'Batch': { icon: FiTag, color: 'text-purple-700 bg-purple-50 border-purple-200' },
      'Course': { icon: FiTag, color: 'text-blue-700 bg-blue-50 border-blue-200' },
      'Book': { icon: FiTag, color: 'text-amber-700 bg-amber-50 border-amber-200' },
      'Boost': { icon: FiTrendingUp, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
      'Banner': { icon: FiGrid, color: 'text-rose-700 bg-rose-50 border-rose-200' },
      'DonateToExpert': { icon: FiDollarSign, color: 'text-indigo-700 bg-indigo-50 border-indigo-200' }
    };
    const config = typeConfig[type] || { icon: FiTag, color: 'text-gray-700 bg-gray-50 border-gray-200' };
    const Icon = config.icon;
    
    return (
      <span className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {type || 'N/A'}
      </span>
    );
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100/80 overflow-hidden">
        {/* Header Section */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 md:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
              <FiDollarSign className="w-4 h-4 text-[#715EFE]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">{TbaleName}</h2>
              <p className="text-xs text-gray-400 hidden sm:block">
                {TbaleData?.length || 0} transactions
              </p>
            </div>
          </div>
        </div>

        {/* Filter Bar - Fixed Alignment */}
        <div className="p-4 md:p-5 border-b border-gray-100 bg-white">
          <div className="flex flex-wrap items-end gap-3">
            {/* Search - Takes remaining space */}
            <div className="relative flex-1 min-w-[180px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user name..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                value={filter?.keyWord || ''}
                onChange={(e) => handleSetFilter("keyWord", e.target.value)}
              />
            </div>

            {/* Type Filter */}
            <div className="relative min-w-[120px]">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiFilter className="text-gray-400 w-4 h-4" />
              </div>
              <select 
                onChange={(e) => handleSetFilter("type", e.target.value)} 
                className="appearance-none w-full h-10 pl-9 pr-8 py-2 bg-white border border-gray-200 text-sm text-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                value={filter?.type || ''}
              >
                <option value=''>All Types</option>
                <option value='Batch'>Batch</option>
                <option value='Course'>Course</option>
                <option value='Book'>Book</option>
                <option value='Boost'>Boost</option>
                <option value='Banner'>Banner</option>
                <option value='DonateToExpert'>DonateToExpert</option>
              </select>
              <FiClock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Status Filter */}
            <div className="relative min-w-[120px]">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiFilter className="text-gray-400 w-4 h-4" />
              </div>
              <select 
                onChange={(e) => handleSetFilter("status", e.target.value)} 
                className="appearance-none w-full h-10 pl-9 pr-8 py-2 bg-white border border-gray-200 text-sm text-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                value={filter?.status || ''}
              >
                <option value=''>All Status</option>
                <option value='SUCCESS'>Success</option>
                <option value='REFUND'>Refund</option>
                <option value='FAILED'>Failed</option>
                <option value='PENDING'>Pending</option>
              </select>
              <FiClock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Date Range - With Calendar Icons */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                {/* From Date with Calendar Icon */}
                <div className="relative">
                  <DatePicker
                    label={
                      <div className="flex items-center gap-1.5">
                        <FiCalendar className="w-3.5 h-3.5 text-gray-400" />
                        <span>From</span>
                      </div>
                    }
                    date={filter.fromDate}
                    onDateChange={(value) => handleSetFilter("fromDate", value)}
                    className="w-[140px]"
                  />
                </div>
                
                <span className="text-gray-400 text-xs">→</span>
                
                {/* To Date with Calendar Icon */}
                <div className="relative">
                  <DatePicker
                    label={
                      <div className="flex items-center gap-1.5">
                        <FiCalendar className="w-3.5 h-3.5 text-gray-400" />
                        <span>To</span>
                      </div>
                    }
                    date={filter.toDate}
                    onDateChange={(value) => handleSetFilter("toDate", value)}
                    className="w-[140px]"
                  />
                </div>
              </div>
            </div>

            {/* Download CSV */}
            <CSVLink data={csvData} filename="payment_transactions.csv" className="flex-shrink-0">
              <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#855DF6] to-[#715EFE] rounded-lg hover:shadow-lg hover:shadow-[#715EFE]/25 transition-all duration-200 whitespace-nowrap">
                <PiFileCsvDuotone className="text-lg" />
                <span className="hidden sm:inline">Download CSV</span>
                <span className="sm:hidden">CSV</span>
              </button>
            </CSVLink>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto p-0">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                {TbaleHeader.map((heading, index) => {
                  const headerIcons = {
                    '#': null,
                    'Transaction ID': FiTag,
                    'Payment Type': FiGrid,
                    'User Name ': FiUser,
                    'Date': FiCalendar,
                    'Status': FiClock,
                    'Amount': FiDollarSign,
                    'Action': null
                  };
                  const Icon = headerIcons[heading.label] || headerIcons[heading.label?.trim()];
                  return (
                    <td key={index} className="px-3 md:px-4 py-3 whitespace-nowrap">
                      <SortableHeader
                        label={heading.label}
                        sortKey={heading.sortKey}
                        currentSortKey={currentSortKey}
                        currentSortOrder={currentSortOrder}
                        onSort={onSort}
                        icon={Icon}
                      />
                    </td>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {TbaleData && TbaleData.length > 0 ? (
                TbaleData.map((ele, index) => (
                  <tr key={index} className="hover:bg-gray-50/70 transition-colors duration-150 group">
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm font-medium text-gray-400">
                        {(filter.pageNo - 1) * filter.size + index + 1}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                          <FiTag className="w-3.5 h-3.5 text-[#715EFE]" />
                        </div>
                        <span className="text-xs font-mono text-gray-600">
                          {ele._id?.slice(-6) || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <TypeBadge type={ele.type} />
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                          <span className="text-xs font-bold text-[#715EFE]">
                            {ele?.user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <span className="text-sm text-gray-700 truncate max-w-[100px]">
                          {ele?.user?.fullName || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700 whitespace-nowrap">
                          {ele.createdAt ? moment(ele.createdAt).format('MMM D, YYYY') : 'N/A'}
                        </span>
                        {ele.createdAt && (
                          <span className="text-xs text-gray-400">
                            {moment(ele.createdAt).format('h:mm A')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <StatusBadge status={ele.status} />
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm font-semibold text-emerald-600">
                        ${ele.amount?.toFixed(2) || '0.00'}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="relative">
                        <button 
                          className="p-1.5 text-gray-400 hover:text-[#715EFE] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                          onClick={() => showMenu(ele._id)}
                        >
                          <HiOutlineDotsVertical className="w-4 h-4" />
                        </button>
                        
                        {isMenu && clickedUserId === ele._id && (
                          <div ref={ContentActionRef} className="absolute right-0 z-50 w-48 mt-1 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                            <div className="py-1">
                              <button 
                                className="flex items-center gap-3 px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-[#725EFE33] hover:text-[#715EFE] transition-colors duration-150"
                                onClick={() => viewAction(ele)}
                              >
                                <FiEye className="w-4 h-4" />
                                <span>View Full History</span>
                              </button>
                              {ele?.status !== "REFUND" && ele?.status !== "REFUNDED" && (
                                <button 
                                  className="flex items-center gap-3 px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-[#725EFE33] hover:text-[#715EFE] transition-colors duration-150"
                                  onClick={() => handleRefundPayment(ele)}
                                >
                                  <FiArrowUp className="w-4 h-4 text-amber-500" />
                                  <span>Refund Payment</span>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={TbaleHeader.length} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-4 bg-gray-50 rounded-full">
                        <FiDollarSign className="w-10 h-10 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">No transactions found</p>
                      <p className="text-xs text-gray-400">Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        {total > filter.size && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <div className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-medium">
                {Math.min((filter.pageNo - 1) * filter.size + 1, total)}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(filter.pageNo * filter.size, total)}
              </span>{" "}
              of <span className="font-medium">{total}</span> results
            </div>
            <ReactPaginate
              forcePage={filter.pageNo - 1}
              previousLabel={
                <span className="flex items-center gap-1">
                  <span>Prev</span>
                </span>
              }
              nextLabel={
                <span className="flex items-center gap-1">
                  <span>Next</span>
                </span>
              }
              breakLabel={"..."}
              breakClassName={"px-2 text-gray-400"}
              pageCount={Math.ceil(total / filter.size)}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"flex items-center gap-1"}
              pageClassName={"px-2"}
              pageLinkClassName={"px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200"}
              previousClassName={"px-2"}
              previousLinkClassName={"px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200"}
              nextClassName={"px-2"}
              nextLinkClassName={"px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200"}
              activeClassName={"bg-[#715EFE] text-white rounded-md"}
              activeLinkClassName={"bg-[#715EFE] text-white px-3 py-1 rounded-md"}
              disabledClassName={"opacity-50 cursor-not-allowed"}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default PaymentTransTable